/**
 * One invalidation epoch shared by every permission hook that holds a RESOLVED
 * DECISION in component state (`useContextPermissions`,
 * `useContextViewPermissions`), and by every hook that holds a
 * permission-FILTERED result (`useUnifiedFilterCatalog`'s Measures aggregates).
 *
 * Why an epoch and not just clearing a request map: those hooks' module-scope
 * maps are request COALESCERS — entries are deleted the moment a request
 * settles, so in the steady state the map is empty and clearing it is a no-op.
 * The decision itself lives in each mount's state, so invalidation has to reach
 * mounted components. Hooks subscribe with `useSyncExternalStore` and include
 * the epoch in their resolution key, so a bump re-runs the effect AND makes the
 * pre-existing decision fail the key check in the very render that observes the
 * bump — no one-frame window where the previous identity's answer is surfaced
 * as settled.
 *
 * Why SHARED rather than one epoch per hook: the events that invalidate are
 * identity-shaped (logout, org switch, impersonation switch), and they
 * invalidate every permission decision at once. A per-hook epoch means every
 * new permission hook must remember to build its own and be wired into
 * `useSchemaTypeCacheReset` — which is exactly how the context-level hook came
 * to have no invalidation at all while its view-level twin did (EAINT-789
 * D-4, extended in review pass 4). Over-invalidating costs one refetch;
 * under-invalidating surfaces another identity's permissions.
 */
let permissionEpoch = 0;
const subscribers = new Set<() => void>();

export const subscribeToPermissionInvalidation = (onStoreChange: () => void): (() => void) => {
  subscribers.add(onStoreChange);
  return () => {
    subscribers.delete(onStoreChange);
  };
};

export const getPermissionEpoch = (): number => permissionEpoch;

/**
 * Coalescing maps owned by the hooks that participate in this epoch.
 *
 * The epoch is SHARED, so "each caller clears its own map before bumping" is
 * not a sufficient invariant — a bump triggered by one hook re-runs the effect
 * of EVERY hook, including ones whose map still holds a request issued under
 * the previous identity. `TreeMapOrganism`'s 403 handler is the reachable case:
 * it calls `resetContextViewPermissionsCache()` alone, which bumps the epoch
 * that also invalidates `useContextPermissions` while `inFlightByContext` is
 * untouched, so the pre-bump request is adopted and stored under the new epoch
 * key as freshly resolved — a fail-open. Registering the maps here makes the
 * bump clear all of them atomically, so correctness no longer depends on which
 * reset was called or on React's re-render scheduling.
 */
const coalescingMaps = new Set<{ clear: () => void }>();

export function registerPermissionCoalescingMap(map: { clear: () => void }): void {
  coalescingMaps.add(map);
}

/**
 * Bump the epoch, drop every registered in-flight request, and notify every
 * mounted permission hook.
 */
export function bumpPermissionEpoch(): void {
  coalescingMaps.forEach((map) => map.clear());
  permissionEpoch += 1;
  subscribers.forEach((notify) => notify());
}

/**
 * Stable, serialisable identity for a client OBJECT, so it can take part in a
 * resolution key. `String(client)` is `'[object Object]'` for every instance,
 * so a client cannot be serialised directly; a `WeakMap`-assigned ordinal can,
 * and is collected along with the client it names.
 *
 * The client belongs in the key because a REBUILT client (logout, tenant
 * change, account switch) re-runs the effect without changing the view or
 * the epoch — so a key omitting it stays equal and the render reports the
 * previous identity's decision as settled.
 */
const clientIds = new WeakMap<object, number>();
let nextClientId = 0;

export function identifyClient(client: object): number {
  const existing = clientIds.get(client);
  if (existing !== undefined) return existing;
  nextClientId += 1;
  clientIds.set(client, nextClientId);
  return nextClientId;
}
